import { useCallback, useEffect, useRef, useState } from 'react';
import { readLocalDraft, saveLocalDraft, deleteLocalDraft } from '../services/localDraftStorage';

export default function useRecoverableDraft({ userId, scope, active, value, hasContent, onRestore }) {
  const [view, setView] = useState({ status: 'loading', error: '', pending: null, savedAt: null });
  const session = useRef(null), latest = useRef(null), restore = useRef(onRestore);
  latest.current = { value, hasContent }; restore.current = onRestore;
  const key = userId && scope ? `${userId}:${scope}` : '';
  if (session.current?.key === key && active) session.current.snapshot = latest.current;
  const refresh = useCallback(async () => {
    const current = session.current; if (!current) return;
    try {
      const row = await readLocalDraft(current.userId, current.scope);
      if (session.current !== current) return;
      current.version = row?.version || 0; current.pending = row; current.ready = true; current.blocked = false;
      setView({ status: 'idle', error: '', pending: row, savedAt: row?.savedAt || null });
    } catch (error) { if (session.current === current) { current.blocked = true; setView(old => ({ ...old, status: 'error', error: error.message })); } }
  }, []);
  const persist = useCallback((current = session.current, snapshot = current?.snapshot) => {
    if (!current || !current.ready || current.pending || current.stopped || current.blocked || !snapshot || (!snapshot.hasContent && !current.version) || current.savedValue === snapshot.value) return Promise.resolve();
    const operation = current.chain.then(async () => {
      if (current.stopped || current.blocked || current.savedValue === snapshot.value) return;
      if (session.current === current) setView(old => ({ ...old, status: 'saving', error: '' }));
      try {
        const row = snapshot.hasContent ? await saveLocalDraft(current.userId, current.scope, snapshot.value, current.version) : (await deleteLocalDraft(current.userId, current.scope, current.version), { version: 0, savedAt: null });
        current.version = row.version; current.savedValue = snapshot.value;
        if (session.current === current) setView(old => ({ ...old, status: 'saved', savedAt: row.savedAt, error: '' }));
      } catch (error) {
        current.blocked = error.code === 'DRAFT_CONFLICT';
        if (session.current === current) setView(old => ({ ...old, status: 'error', error: error.message }));
        throw error;
      }
    });
    current.chain = operation.catch(() => {}); return operation;
  }, []);
  useEffect(() => {
    if (!active || !key) { session.current = null; return; }
    const current = { key, userId: String(userId), scope, snapshot: latest.current, version: 0, pending: null, ready: false, stopped: false, blocked: false, chain: Promise.resolve() };
    session.current = current; setView({ status: 'loading', error: '', pending: null, savedAt: null }); refresh();
    return () => { persist(current, current.snapshot).catch(() => {}); if (session.current === current) session.current = null; };
  }, [active, key, userId, scope, refresh, persist]);
  const storageLoading = view.status === 'loading';
  useEffect(() => {
    const current = session.current;
    if (!active || current?.key !== key || view.pending || storageLoading) return;
    const timer = setTimeout(() => persist(current).catch(() => {}), 650);
    return () => clearTimeout(timer);
  }, [active, key, value, hasContent, view.pending, storageLoading, persist]);
  useEffect(() => {
    const flush = () => { if (document.hidden) persist().catch(() => {}); };
    const preventLoss = event => { const current = session.current; if (current && !current.stopped && !current.pending && latest.current?.hasContent && current.savedValue !== latest.current.value) { event.preventDefault(); event.returnValue = ''; } };
    document.addEventListener('visibilitychange', flush); window.addEventListener('beforeunload', preventLoss);
    return () => { document.removeEventListener('visibilitychange', flush); window.removeEventListener('beforeunload', preventLoss); };
  }, [persist]);
  const recover = () => { const current = session.current; if (!current?.pending) return; const row = current.pending; restore.current(row.payload); current.pending = null; setView({ status: 'saved', error: '', pending: null, savedAt: row.savedAt }); };
  const discardStored = async () => {
    const current = session.current; if (!current) return;
    try { await current.chain; await deleteLocalDraft(current.userId, current.scope, current.version); current.version = 0; current.pending = null; current.savedValue = null; current.blocked = false; setView({ status: 'idle', error: '', pending: null, savedAt: null }); }
    catch (error) { setView(old => ({ ...old, error: error.message, status: 'error' })); }
  };
  const clear = async () => {
    const current = session.current; if (!current) return;
    current.stopped = true; await current.chain;
    try { await deleteLocalDraft(current.userId, current.scope, current.version); } catch (error) { if (error.code !== 'DRAFT_CONFLICT') { current.stopped = false; if (session.current === current) setView(old => ({ ...old, status: 'error', error: error.message })); throw error; } }
  };
  const flush = () => {
    const current = session.current;
    if (current && !current.pending && latest.current.hasContent && (!current.ready || current.blocked)) return Promise.reject(new Error(view.error || 'El borrador aún no se ha guardado.'));
    return persist();
  };
  const status = view.status === 'saved' && hasContent && session.current?.savedValue !== value ? 'pending' : view.status;
  const enabled = Boolean(active && key);
  return { ...view, status, pending: enabled ? view.pending : null, error: enabled ? view.error : '', enabled, loading: Boolean(enabled && (session.current?.key !== key || view.status === 'loading')), recover, discardStored, clear, flush, refresh };
}
